import React from "react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip } from "recharts";
import axios from "axios";
import { Link } from "react-router-dom";
import { Brand } from "@/components/Layout";
import { DRIVER_INTERPRETATION, IDR_T } from "@/data/idr";
import { computeIdrResults, idrClinicalReading } from "@/lib/idrScoring";
import { PatientClosing } from "@/components/patient/PatientClosing";
import { IdrEntourage } from "@/components/idr/IdrEntourage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_INK = { fill: "#6E5C53", fontSize: 12 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };
const RADAR_DOMAIN = [0, 100];

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-6">
    <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{children}</h3>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

const DriverBar = ({ row }) => (
  <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-4 print-break" data-testid={`driver-row-${row.key}`}>
    <div className="flex items-baseline justify-between mb-1">
      <h4 className="font-serif-title text-2xl" style={{ color: row.color }}>{row.name}</h4>
      <span className="font-semibold text-[var(--ink)]">{row.score} %</span>
    </div>
    <p className="text-xs text-[var(--ink-soft)] mb-3">{row.description}</p>
    <div className="h-2.5 w-full rounded-full bg-[var(--beige)] overflow-hidden mb-2">
      <div className="h-full rounded-full" style={{ width: `${row.score}%`, backgroundColor: row.color }} />
    </div>
    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: row.interp.color }}>{row.interp.label}</span>
  </div>
);

export const IdrResults = ({ answers, onRestart, lang = "fr" }) => {
  const t = IDR_T[lang];
  const results = React.useMemo(() => computeIdrResults(answers, lang), [answers, lang]);
  const reading = React.useMemo(() => idrClinicalReading(results, lang), [results, lang]);

  React.useEffect(() => {
    axios.post(`${API}/idr-evaluations`, { answers }).catch((e) => console.error("save idr failed", e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="animate-fade-up print-container" data-testid="idr-results-view">
      <div className="hidden print:block mb-6"><Brand /></div>

      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{t.thanks}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.thanksText}</p>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={t.radarSub}>{t.radarTitle}</SectionTitle>
        <ResponsiveContainer width="100%" height={360}>
          <RadarChart data={results.radarData} outerRadius="72%">
            <PolarGrid stroke="#d8ccbe" />
            <PolarAngleAxis dataKey="subject" tick={TICK_INK} />
            <PolarRadiusAxis domain={RADAR_DOMAIN} tick={TICK_MUTED} angle={90} />
            <Radar dataKey="value" stroke="#B58C67" fill="#B58C67" fillOpacity={0.45} />
            <Tooltip formatter={(v) => `${v} %`} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="mb-10">
        <SectionTitle sub={t.driversSub}>{t.driversTitle}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.sorted.map((row) => (<DriverBar key={row.key} row={row} />))}
        </div>
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break" data-testid="idr-interpretation">
        <SectionTitle sub={t.interpSub}>{t.interpTitle}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DRIVER_INTERPRETATION.map((r) => (
            <div key={r.min} className="flex items-center gap-3 rounded-xl bg-[var(--sand)] border border-[var(--border)] p-3">
              <span className="inline-flex items-center justify-center h-9 w-20 rounded-lg text-white text-sm font-semibold shrink-0" style={{ backgroundColor: r.color }}>{r.min}–{r.max} %</span>
              <span className="text-sm text-[var(--ink)]">{r[lang]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-9 mb-10 print-break">
        <h3 className="font-serif-title text-3xl mb-4">{t.clinicalTitle}</h3>
        <p className="leading-relaxed text-[0.98rem]" data-testid="idr-clinical-reading">{reading}</p>
        <p className="text-xs text-white/80 mt-4">{t.clinicalNote}</p>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-7 mb-10 text-center no-print" data-testid="idr-continue-exo">
        <p className="text-[var(--ink)] mb-4">{t.continueExo}</p>
        <Link to="/drivers-exo" className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105 transition-all">{t.continueExoBtn}</Link>
      </div>

      <div className="mb-10" data-testid="idr-entourage-section">
        <IdrEntourage variant="full" patientAnswers={answers} lang={lang} />
      </div>

      <PatientClosing lang={lang} />

      <div className="flex flex-wrap items-center justify-center gap-4 no-print">
        <button onClick={() => window.print()} data-testid="idr-btn-export-pdf" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.exportPdf}</button>
        <button onClick={onRestart} data-testid="idr-btn-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.restart}</button>
      </div>
    </div>
  );
};
